import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BRAND } from "@/config";

const letters = BRAND.name.toUpperCase().split("");

export const Preloader = () => {
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDone(true), 1500);
    return () => clearTimeout(t);
  }, []);

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          data-testid="preloader"
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, filter: "blur(6px)" }}
          transition={{ duration: 0.6, ease: [0.22, 0.7, 0.2, 1] }}
        >
          {/* Globe mark */}
          <motion.div
            className="relative mb-8 h-20 w-20"
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <span className="absolute inset-0 rounded-full bg-blue-500/20 blur-xl" />
            <motion.span
              className="absolute inset-0 rounded-full border-2 border-blue-500/30"
              animate={{ rotate: 360 }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "linear" }}
              style={{ borderTopColor: "#2563eb", borderRightColor: "#60a5fa" }}
            />
            <motion.span
              className="absolute inset-[6px] rounded-full border border-sky-400/40"
              animate={{ rotate: -360 }}
              transition={{ duration: 3.2, repeat: Infinity, ease: "linear" }}
              style={{ borderBottomColor: "#38bdf8" }}
            />
            <span className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-sky-400 to-blue-600 shadow-[0_0_14px_2px_rgba(37,99,235,0.6)]" />
          </motion.div>

          {/* Wordmark with staggered letters */}
          <div className="flex items-end font-wordmark text-2xl tracking-tight text-slate-900 sm:text-3xl">
            {letters.map((ch, i) => (
              <motion.span
                key={i}
                initial={{ y: 18, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.15 + i * 0.03, duration: 0.4, ease: "easeOut" }}
                className={ch === " " ? "w-2" : ""}
              >
                {ch === " " ? "\u00A0" : ch}
              </motion.span>
            ))}
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15 + letters.length * 0.03, duration: 0.3 }}
              className="text-blue-600"
            >
              .
            </motion.span>
          </div>

          {/* Slogan */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="mt-3 font-mono2 text-[10px] uppercase tracking-[0.4em] text-slate-400"
          >
            {BRAND.slogan}
          </motion.p>

          {/* Progress line */}
          <div className="mt-7 h-[3px] w-40 overflow-hidden rounded-full bg-slate-100">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-sky-400 via-blue-600 to-sky-400"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: 1.2, ease: "easeInOut" }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Preloader;
